// src/components/Admin/TaskManager/dialogs/TaskDialog.tsx

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Chip,
  FormControlLabel,
  Checkbox,
  Typography,
  Tabs,
  Tab,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  ScheduledTask,
  INTERVAL_TYPES,
  TASK_TEMPLATES,
  TASK_TYPES,
  TIMEZONE_OPTIONS,
  NOTIFICATION_TYPE_OPTIONS,
  TaskDefinition,
} from '../../../types/taskManager';
import { HbrDivider } from '@harbor/elements-react-standalone';

interface Props {
  open: boolean;
  task: ScheduledTask | null;
  onClose: () => void;
  onSave: (data: Partial<ScheduledTask>) => Promise<void>;
  isSaving: boolean;
}

const TaskDialog: React.FC<Props> = ({ open, task, onClose, onSave, isSaving }) => {
  const [mode, setMode] = useState<'template' | 'json'>('template');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('approval_notification');
  const [formData, setFormData] = useState<Partial<ScheduledTask>>({});
  const [newTag, setNewTag] = useState('');
  const [rawJson, setRawJson] = useState('');

  useEffect(() => {
    if (task) {
      setFormData(task);
      setRawJson(JSON.stringify(task, null, 2));
      setMode('template');
    } else {
      const template = TASK_TEMPLATES[selectedTemplate];
      const defaultData = {
        ...template,
        start_time: new Date().toISOString(),
        enabled: true,
        created_by: '',
      };
      setFormData(defaultData);
      setRawJson(JSON.stringify(defaultData, null, 2));
    }
  }, [task, open, selectedTemplate]);

  const handleTemplateChange = (templateKey: string) => {
    setSelectedTemplate(templateKey);
    const template = TASK_TEMPLATES[templateKey];
    const data = {
      ...template,
      start_time: new Date().toISOString(),
      enabled: true,
    };
    setFormData(data);
    setRawJson(JSON.stringify(data, null, 2));
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTaskChange = (index: number, field: string, value: any) => {
    const tasks = [...(formData.job_payload?.tasks || [])];
    tasks[index] = { ...tasks[index], [field]: value };
    handleChange('job_payload', {
      ...formData.job_payload,
      tasks
    });
  };

  const handleAddTask = () => {
    const newTask: TaskDefinition = {
      task_id: `task_${Date.now()}`,
      task_type: 'approval_notification',
    };
    const currentTasks = formData.job_payload?.tasks || [];
    handleChange('job_payload', {
      ...formData.job_payload,
      tasks: [...currentTasks, newTask],
    });
  };

  const handleDeleteTask = (index: number) => {
    const tasks = [...(formData.job_payload?.tasks || [])];
    tasks.splice(index, 1);
    handleChange('job_payload', { ...formData.job_payload, tasks });
  };

  const handleAddTag = () => {
    if (newTag && !formData.tags?.includes(newTag)) {
      handleChange('tags', [...(formData.tags || []), newTag]);
      setNewTag('');
    }
  };

  const handleDeleteTag = (tagToDelete: string) => {
    handleChange('tags', formData.tags?.filter(tag => tag !== tagToDelete));
  };

  const handleSave = async () => {
    if (mode === 'json') {
      try {
        const parsedData = JSON.parse(rawJson);
        await onSave(parsedData);
      } catch (error) {
        alert('Invalid JSON format');
        return;
      }
    } else {
      await onSave(formData);
    }
  };

  const handleCopyJson = () => {
    navigator.clipboard.writeText(JSON.stringify(formData, null, 2));
    alert('JSON copied to clipboard!');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {task ? 'Edit Scheduled Task' : 'Create Scheduled Task'}
          </Typography>
          <Tabs value={mode} onChange={(e, v) => setMode(v)} sx={{ minHeight: 40 }}>
            <Tab label="Form" value="template" />
            <Tab label="JSON" value="json" />
          </Tabs>
        </Box>
      </DialogTitle>

      <DialogContent>
        {mode === 'template' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            {/* Template Selector */}
            {!task && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Select Template
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {Object.entries(TASK_TEMPLATES).map(([key, template]) => (
                    <Chip
                      key={key}
                      label={template.name}
                      onClick={() => handleTemplateChange(key)}
                      color={selectedTemplate === key ? 'primary' : 'default'}
                      variant={selectedTemplate === key ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>

                {selectedTemplate === 'approval_notification' && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="caption">
                      <strong>Approval Notification:</strong> Sends Webex cards to approvers for pending approval requests.
                      Runs daily and groups notifications by approver.
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}

            {/* Basic Info */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Basic Information</Typography>
              <HbrDivider className="pb-2" />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Task Name"
                  value={formData.name || ''}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  fullWidth
                  helperText="Unique name for this scheduled task"
                />

                <TextField
                  label="Description"
                  value={formData.description || ''}
                  onChange={(e) => handleChange('description', e.target.value)}
                  multiline
                  rows={2}
                  fullWidth
                />

                <TextField
                  label="Job Type"
                  value={formData.job_type || ''}
                  onChange={(e) => handleChange('job_type', e.target.value)}
                  required
                  fullWidth
                  helperText="Type of job (e.g., approval_notification)"
                />
              </Box>
            </Box>

            {/* Schedule */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Schedule Configuration</Typography>
              <HbrDivider className="pb-2" />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    select
                    label="Interval Type"
                    value={formData.interval_type || 'daily'}
                    onChange={(e) => handleChange('interval_type', e.target.value)}
                    fullWidth
                  >
                    {INTERVAL_TYPES.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    label="Interval Value"
                    type="number"
                    value={formData.interval_value || 1}
                    onChange={(e) => handleChange('interval_value', parseInt(e.target.value) || 1)}
                    fullWidth
                    disabled={formData.interval_type === 'once' || formData.interval_type === 'cron'}
                    helperText="Multiplier (e.g., 2 = every 2 days)"
                  />
                </Box>

                {formData.interval_type === 'cron' && (
                  <TextField
                    label="Cron Expression"
                    value={formData.cron_expression || ''}
                    onChange={(e) => handleChange('cron_expression', e.target.value)}
                    placeholder="0 9 * * *"
                    helperText="Format: minute hour day month day-of-week"
                    fullWidth
                  />
                )}

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="Start Time"
                    type="datetime-local"
                    value={formData.start_time?.slice(0, 16) || ''}
                    onChange={(e) => handleChange('start_time', new Date(e.target.value).toISOString())}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />

                  <TextField
                    label="End Time (Optional)"
                    type="datetime-local"
                    value={formData.end_time?.slice(0, 16) || ''}
                    onChange={(e) => handleChange('end_time', e.target.value ? new Date(e.target.value).toISOString() : undefined)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Box>

                <TextField
                  select
                  label="Timezone"
                  value={formData.timezone_name || 'UTC'}
                  onChange={(e) => handleChange('timezone_name', e.target.value)}
                  fullWidth
                >
                  {TIMEZONE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>

            {/* Settings */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Settings</Typography>
              <HbrDivider className="pb-2" />

              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.enabled || false}
                      onChange={(e) => handleChange('enabled', e.target.checked)}
                    />
                  }
                  label="Enabled"
                />

                <TextField
                  label="Max Failures"
                  type="number"
                  value={formData.max_failures || 3}
                  onChange={(e) => handleChange('max_failures', parseInt(e.target.value) || 3)}
                  sx={{ width: 150 }}
                  size="small"
                  helperText="Auto-disable after N failures"
                />
              </Box>
            </Box>

            {/* Tags */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Tags</Typography>
              <HbrDivider />

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                {formData.tags?.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => handleDeleteTag(tag)}
                    size="small"
                  />
                ))}
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Add tag"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                  sx={{ flexGrow: 1 }}
                />
                <Button onClick={handleAddTag} disabled={!newTag} size="small">
                  Add
                </Button>
              </Box>
            </Box>

            {/* Tasks */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Job Tasks</Typography>
              <HbrDivider className="pb-2" />

              {formData.job_payload?.tasks?.map((taskItem: TaskDefinition, index: number) => (
                <Accordion key={index}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Typography variant="body2">
                        Task {index + 1}: {taskItem.task_id || 'New Task'}
                      </Typography>
                      <Chip label={taskItem.task_type || 'No type'} size="small" />
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        label="Task ID"
                        value={taskItem.task_id || ''}
                        onChange={(e) => handleTaskChange(index, 'task_id', e.target.value)}
                        fullWidth
                        size="small"
                        required
                      />

                      <TextField
                        select
                        label="Task Type"
                        value={taskItem.task_type || ''}
                        onChange={(e) => handleTaskChange(index, 'task_type', e.target.value)}
                        fullWidth
                        size="small"
                        required
                      >
                        {TASK_TYPES.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            <Box>
                              <Typography variant="body2">{option.label}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.description}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </TextField>

                      {/* Approval Notification Fields */}
                      {taskItem.task_type === 'approval_notification' && (
                        <>
                          <TextField
                            select
                            label="Notification Type"
                            value={(taskItem as any).notification_type || 'summary'}
                            onChange={(e) => handleTaskChange(index, 'notification_type', e.target.value)}
                            fullWidth
                            size="small"
                          >
                            {NOTIFICATION_TYPE_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                <Box>
                                  <Typography variant="body2">{option.label}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {option.description}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </TextField>

                          <TextField
                            label="Minimum Age (Days)"
                            type="number"
                            value={(taskItem as any).min_age_days || 0}
                            onChange={(e) => handleTaskChange(index, 'min_age_days', parseInt(e.target.value) || 0)}
                            fullWidth
                            size="small"
                            helperText="Only send for approvals older than N days (0 = all pending)"
                          />

                          <TextField
                            label="Base URL"
                            value={(taskItem as any).base_url || window.location.origin}
                            onChange={(e) => handleTaskChange(index, 'base_url', e.target.value)}
                            fullWidth
                            size="small"
                            helperText="Base URL for approval links in notifications"
                          />
                        </>
                      )}

                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteTask(index)}
                        variant="outlined"
                      >
                        Remove Task
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}

              <Button
                startIcon={<AddIcon />}
                onClick={handleAddTask}
                variant="outlined"
                fullWidth
                sx={{ mt: 1 }}
              >
                Add Task
              </Button>
            </Box>

            {/* Quick Actions */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
              <Button
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyJson}
                variant="outlined"
              >
                Copy JSON
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setRawJson(JSON.stringify(formData, null, 2));
                  setMode('json');
                }}
                variant="outlined"
              >
                View as JSON
              </Button>
            </Box>
          </Box>
        ) : (
          /* JSON Mode */
          <Box sx={{ mt: 1 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="caption">
                <strong>JSON Mode:</strong> Edit the raw configuration. Ensure valid JSON before saving.
              </Typography>
            </Alert>

            <TextField
              multiline
              rows={25}
              value={rawJson}
              onChange={(e) => setRawJson(e.target.value)}
              fullWidth
              variant="outlined"
              sx={{
                '& textarea': {
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                },
              }}
            />

            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button
                size="small"
                onClick={() => {
                  try {
                    const parsed = JSON.parse(rawJson);
                    setFormData(parsed);
                    setMode('template');
                  } catch (error) {
                    alert('Invalid JSON format');
                  }
                }}
                variant="outlined"
              >
                Switch to Form View
              </Button>

              <Button
                size="small"
                onClick={() => {
                  try {
                    const parsed = JSON.parse(rawJson);
                    setRawJson(JSON.stringify(parsed, null, 2));
                    alert('JSON formatted');
                  } catch (error) {
                    alert('Invalid JSON - cannot format');
                  }
                }}
                variant="outlined"
              >
                Format JSON
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaving || (mode === 'template' && (!formData.name || !formData.job_type))}
        >
          {isSaving ? 'Saving...' : 'Save Task'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskDialog;